import React from 'react';
import { connect } from 'react-redux';
import Highcharts, { color } from 'highcharts';
import Exporting from 'highcharts/modules/exporting';
import OfflineExporting from 'highcharts/modules/offline-exporting.src';
import moment from 'moment';
import _ from 'lodash';

import withChartExport from '../../../components/ChartExporting';
import { intersectRect } from '../../../util/svg';
import dateUtils from '../../../util/dates';
import dataUtils from '../../../util/dataUtils';
import { escapeHTML } from '../../../util/text';
import chartUtils from '../../../util/chartUtils';
import { WALLBOARD_TEXT_SIZE } from '../../../util/wallboardFrame';

import exportIcon from '../../../assets/img/export.svg';

import highchartFactory from '../../../util/highcharts';
const highchartUtils = highchartFactory(Highcharts);

const labelFont = (wallmode, appStyle) => (wallmode ? { fontSize: WALLBOARD_TEXT_SIZE, fontWeight: 500 } : { fontSize: '12px', ...appStyle });

const RATE_LABEL_GAP = 4;

function boxesOverlap(a, b) {
  return a.x < b.x + b.width + RATE_LABEL_GAP && a.x + a.width + RATE_LABEL_GAP > b.x && a.y < b.y + b.height + RATE_LABEL_GAP && a.y + a.height + RATE_LABEL_GAP > b.y;
}

function placeRateLabel(chart, propName, endX, endY) {
  const { label } = chart[propName];

  const container = chart.container.getBoundingClientRect();
  const scale = container.width / chart.chartWidth || 1;
  const card = chart.container.closest('.diagram_details');
  const buttons = _.compact([_.get(_.first(chart.exportSVGElements), 'element'), card && card.querySelector('.chart-download')])
    .map(element => element.getBoundingClientRect())
    .filter(drawn => drawn.width)
    .map(drawn => ({
      x: (drawn.left - container.left) / scale,
      y: (drawn.top - container.top) / scale,
      width: drawn.width / scale,
      height: drawn.height / scale
    }));

  const other = chart[propName === 'departure' ? 'arrival' : 'departure'];
  const otherBox = other && other.label.visibility !== 'hidden' ? { x: other.label.x, y: other.label.y, width: other.label.width, height: other.label.height } : null;

  const boxAt = y => {
    const box = { x: endX - label.width + 5, y, width: label.width, height: label.height };
    buttons.filter(button => boxesOverlap(box, button)).forEach(button => {
      box.x = button.x - RATE_LABEL_GAP - box.width;
    });
    return box;
  };
  const fits = box => box.y >= chart.plotTop && box.y + box.height <= chart.plotTop + chart.plotHeight && !(otherBox && boxesOverlap(box, otherBox));
  const above = boxAt(endY - label.height + 2);
  const below = boxAt(endY - 2);
  const boxes = propName === 'departure' ? [below, above] : [above, below];
  const box = _.find(boxes, fits) || boxes[0];

  label.attr({ x: box.x, y: box.y });
}

function drawLine(chart, propName, label, serie, labelStyle) {
  if (!serie.data.length) {
    return true;
  }

  let index =
    _.findIndex(serie.data, s => {
      return s.y > 0;
    }) - 1;
  index = index < 0 ? 0 : index;
  const firstPointWithValue = serie.data[index];
  const pointA_x = firstPointWithValue.plotX + chart.plotLeft;
  const pointA_y = firstPointWithValue.plotY + chart.plotTop;

  const lastPoint = _.last(serie.data);
  const pointB_x = lastPoint.plotX + chart.plotLeft;
  const pointB_y = lastPoint.plotY + chart.plotTop;

  if (!chart[propName]) {
    chart[propName] = {};
    chart[propName].line = chart.renderer
      .path(['M', pointA_x, pointA_y, 'L', pointB_x - pointA_x, pointB_y])
      .attr({
        zIndex: 3,
        stroke: '#545D66',
        'stroke-width': 1.5,
        'stroke-dasharray': '4,4'
      })
      .add();
    chart[propName].label = chart.renderer
      .label(label, pointB_x, pointB_y, 'rect', null, null, false, null)
      .addClass('chart-annotation')
      .css({
        color: '#545D66',
        textOutline: '3px #FFFFFF',
        ...labelStyle
      })
      .attr({
        text: label,
        padding: 5,
        zIndex: 3,
        fill: 'none'
      })
      .add();
  }

  chart[propName].line
    .attr({
      d: 'M ' + pointA_x + ' ' + pointA_y + ' L ' + pointB_x + ' ' + pointB_y
    })
    .show();
  chart[propName].label.attr({ text: label }).show();
  placeRateLabel(chart, propName, pointB_x, pointB_y);
}

function destroyLine(chart, propName) {
  if (chart[propName]) {
    chart[propName].line.hide();
    chart[propName].label.hide();
  }
}

let chartConfig = {
  credits: {
    enabled: false
  },
  chart: {
    backgroundColor: null,
    type: 'area',
    zoomType: 'xy',
    spacingBottom: 10,
    marginRight: 10,
    // room for "N items" beside the plot
    marginLeft: 84,
    events: {
      load: e => {
        e.target.options.exporting.sourceHeight = e.target.chartHeight;
        e.target.options.exporting.sourceWidth = e.target.chartWidth;
      }
    }
  },
  title: {
    text: ''
  },
  tooltip: {
    useHTML: true,
    shadow: false,
    split: true,
    hideDelay: 0,
    borderRadius: 4,
    borderColor: '#DEE0E2',
    borderWidth: 0,
    backgroundColor: '#FFF',
  },
  xAxis: {
    type: 'datetime',
    tickmarkPlacement: 'on',
    title: {
      enabled: false
    },
    labels: {
      style: {
        color: chartUtils.TREND_LABEL_COLOR,
        fontSize: '12px'
      }
    },
    lineColor: chartUtils.TREND_AXIS_LINE_COLOR,
    tickColor: chartUtils.TREND_AXIS_LINE_COLOR,
    gridLineWidth: 1,
    gridLineColor: chartUtils.TREND_GRID_COLOR,
    crosshair: { width: 1, color: chartUtils.TREND_LABEL_COLOR, zIndex: 4 },
    events: {}
  },
  yAxis: {
    opposite: false,
    allowDecimals: false,
    // the unit sits in the labels
    title: {
      text: null
    },
    labels: {
      zIndex: 2,
      formatter: function() {
        return chartUtils.withUnit(this.value, 'item');
      },
      style: {
        color: chartUtils.TREND_LABEL_COLOR,
        fontSize: '12px',
        whiteSpace: 'nowrap'
      }
    },
    gridLineWidth: 1,
    gridLineColor: chartUtils.TREND_GRID_COLOR
  },
  legend: {
    enabled: false
  },
  plotOptions: {
    area: {
      stacking: 'normal',
      fillOpacity: 1,
      lineWidth: 1,
      states: { hover: { lineWidthPlus: 0 } },
      marker: {
        enabled: false,
        states: {
          hover: {
            enabled: false
          }
        }
      },
      events: {
        mouseOut: function() {
          if (this.chart.cross) {
            this.chart.cross.hide();
          }
          if (this.chart.pLbls) {
            _.forEach(this.chart.pLbls, lbl => {
              if (lbl) {
                lbl.hide();
                lbl.circle.hide();
              }
            });
          }
        }
      }
    },
    series: {
      turboThreshold: 0,
      marker: {
        enabled: false
      },
      events: {}
    }
  },
  series: [],
};

export class CFDChart extends React.Component {
  constructor(props) {
    super(props);
    this.state = {};
  }

  chartRendered = false;
  componentDidMount() {
    highchartUtils.setDefaults();
    Exporting(Highcharts);
    OfflineExporting(Highcharts);
    highchartUtils.splitTooltipOverride();

    let that = this;
    chartConfig.tooltip.formatter = function(ev) {
      return that.tooltipFormatter(ev, this, that);
    };
    chartConfig.chart.events.redraw = function(ev) {
      return that.chartRendered && that.drawArrivalLine(ev);
    };
    chartConfig.chart.events.render = function() {
      chartUtils.drawStateEdges(this, series => series.options.lineColor);
    };

    chartConfig.exporting = this.props.chartExporting;

    this.chart = new Highcharts['Chart'](`${this.props.id}-details`, chartConfig);

    this.updateTimeLabelsFormat(this.props.dateFormat);

    if (this.props.data && this.props.data.length) {
      this.updateChart();
    }

    if (this.props.arrivalDepartureLines) {
      this.drawArrivalLine({ target: this.chart });
    }

    if (this.props.wallmode) {
      this.updateChartFont();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.data != this.props.data) {
      this.updateChart(prevProps.data);
    }

    if (prevProps.arrivalDepartureLines != this.props.arrivalDepartureLines || prevProps.stats != this.props.stats) {
      this.drawArrivalLine({ target: this.chart });
    }

    if (prevProps.dateFormat != this.props.dateFormat) {
      this.updateTimeLabelsFormat(this.props.dateFormat);
    }

    if (prevProps.chartExporting != this.props.chartExporting) {
      this.chart.update(
        {
          exporting: {
            filename: this.props.chartExporting.filename,
            chartOptions: {
              title: {
                text: 'Cumulative Flow Diagram',
                style: {
                  fontSize: '16px',
                  fontWeight: 'bold'
                }
              },          
            }
          }
        },
        false
      );
    }

    if (prevProps.excludeWeekends != this.props.excludeWeekends) {
      this.renderWeekends();
    }
  }

  componentWillUnmount() {
    this.chart.destroy();
  }

  updateChart(oldData = []) {
    const diff = dataUtils.getDiff(chartUtils.stateSeries(oldData, 'lineColor'), chartUtils.stateSeries(this.props.data, 'lineColor'), 'data');
    highchartUtils.updateSeries(diff, this.chart);
    this.chart.redraw(false);
    this.chartRendered = true;

    setTimeout(() => {
      this.updateChartRange();
    }, 1000);
  }

  updateChartFont() {
    const newConfig = highchartUtils.getFontSizeConfig('cfd');
    this.chart.update({
      ...newConfig.chart,
      // the TV's larger axis labels take the room they need
      chart: { marginLeft: null }
    });
  }

  updateTimeLabelsFormat(dateFormat) {
    let config = { xAxis: {} };
    config.xAxis.dateTimeLabelFormats = dateUtils.createHighchartFormats(dateFormat);

    this.chart.update(config);
  }

  updateChartRange() {
    this.renderWeekends();
    this.chart.xAxis[0].setExtremes(+this.props.dates.startDate, +this.props.dates.endDate);
  }

  renderWeekends() {
    const bands = [];
    if (this.props.excludeWeekends) {
      const endDate = new Date(this.props.dates.endDate);
      const date = new Date(this.props.dates.startDate);
      while (date < endDate) {
        if (date.getDay() === 5 || date.getDay() === 6) {
          const day = new Date(date);
          bands.push({
            from: day.startOfDay(),
            to: day.endOfDay(),
            color: 'rgba(84, 93, 102, 0.07)',
            zIndex: 3
          });
        }
        date.setDate(date.getDate() + 1);
      }
    }

    this.chart.update({
      xAxis: { plotBands: bands }
    });
  }

  tooltipFormatter(ev, chartScope, componentScope) {
    var s = [];
    s.push(moment(chartScope.x).format(componentScope.props.dateFormat));

    if (componentScope.props.wipStagesTooltip == 'stages') {
      let tooltipHtml = '';
      chartScope.points.forEach(point => {
        tooltipHtml += `<div class="tooltip-label-item"><span class="label-box" style="background:${point.color}"></span>${escapeHTML(point.series.name)}: ${point.point.y} </div>`;
      });
      s.push(tooltipHtml);
    } else {
      s.push(
        `<span>Process: ${_(chartScope.points)
          .filter(p => !p.point.isDoneState)
          .sumBy('point.daily')}</span>`
      );
    }

    componentScope.renderCrosshair(ev, chartScope.points, componentScope.props.approxAvgStagesTooltip);
    return s;
  }

  //Approx average cycle time lines renderer
  renderCrosshair(ev, points, approxAvgStagesTooltip) {
    let chart = ev.chart;
    let height = 0;
    let lineOpacity = 0.6;
    const symbolSize = 12;

    if (!chart.cross) {
      chart.cross = chart.renderer
        .path()
        .attr({
          stroke: `rgba(0, 26, 51, ${lineOpacity})`,
          'stroke-width': 1,
          zIndex: 3
        })
        .add();
    }
    if (!chart.pLbls) {
      chart.pLbls = [];
    }

    let path = [];

    let tooltipPoints;

    if (approxAvgStagesTooltip != 'system') {
      tooltipPoints = points;
    } else {
      tooltipPoints = [points[0], points[points.length - 1]];
    }

    _.forEach(tooltipPoints, (point, index) => {
      let p = point.point;
      let x = p.plotX + chart.plotLeft;
      let y = p.plotY + chart.plotTop;
      let color;

      if (index === 0) {
        height = chart.plotSizeY - p.plotY;
        path = ['M', x, y, 'v', 0, height];
      }

      if (index > 0) {
        let pIndex = _.indexOf(p.series.points, p);
        let crossPointX = 0,
          approxAvgCycleTime = 0;

        let bandFound = false,
          bandIndex = index - 1;
        while (!bandFound && bandIndex > 0) {
          let bandLeftPoints = points[bandIndex].point.series.points.slice(0, pIndex + 1);
          bandFound = _.sumBy(bandLeftPoints, 'daily') > 0;
          if (!bandFound) {
            bandIndex -= 1;
          }
        }

        color = points[index - 1].point.color;
        let leftPoints = points[bandIndex].point.series.points.slice(0, pIndex + 1);
        for (let lp = leftPoints.length - 1; lp >= 0; lp--) {
          let prevLp = lp > 0 ? lp - 1 : 0;
          let leftPoint = leftPoints[lp],
            prevLeftPoint = leftPoints[prevLp];
          if (leftPoint.plotY == p.plotY) {
            crossPointX = x - leftPoint.plotX - chart.plotLeft;
            approxAvgCycleTime = Math.round((p.x - leftPoints[lp].x) / 8.64e7);
          } else if (leftPoint.plotY <= p.plotY && prevLeftPoint.plotY >= p.plotY) {
            const share = prevLeftPoint.plotY === leftPoint.plotY ? 0 : (prevLeftPoint.plotY - p.plotY) / (prevLeftPoint.plotY - leftPoint.plotY);
            crossPointX = x - (prevLeftPoint.plotX + share * (leftPoint.plotX - prevLeftPoint.plotX)) - chart.plotLeft;
            approxAvgCycleTime = Math.round((p.x - leftPoints[prevLp].x) / 8.64e7);
          }
        }

        path = path.concat(['M', x, y, 'h', -crossPointX, 0]);

        let approxAvgCycleTimeLbl = approxAvgCycleTime + ' day' + (approxAvgCycleTime != 1 ? 's' : '');

        if (!chart.pLbls[index]) {
          chart.pLbls[index] = chart.renderer
            .label(approxAvgCycleTime, x - crossPointX, y, 'rect', null, null, false, null)
            .addClass('chart-annotation')
            .css({
              color: '#545D66',
              ...labelFont(this.props.wallmode)
            })
            .attr({
              padding: 4,
              r: 8,
              zIndex: 4,
              stroke: '#D5E7F2',
              'stroke-width': 1
            })
            .add();

          chart.pLbls[index].circle = chart.renderer
            .symbol('circle', x - crossPointX, y, symbolSize, symbolSize)
            .addClass('chart-marker')
            .attr({
              zIndex: 5,
              stroke: '#fff',
              'stroke-width': 2
            })
            .add();
        }
        let lbl = chart.pLbls[index],
          circle = lbl.circle,
          prevLbl = chart.pLbls[index > 1 ? index - 1 : 1];

        lbl.attr({ text: approxAvgCycleTimeLbl });

        let xLblPos = x - (crossPointX > 0 ? crossPointX : 10) - lbl.width - 20;
        let yLblPos = y - lbl.height / 2;

        if (xLblPos < 0) {
          xLblPos = 0;
        }

        if (index > 1 && intersectRect(prevLbl, lbl)) {
          yLblPos = prevLbl.y + prevLbl.height + 3;
        }

        if (approxAvgCycleTime > 0) {
          lbl
            .attr({
              text: approxAvgCycleTimeLbl,
              fill: '#fff',
              x: xLblPos,
              y: yLblPos
            })
            .show();

          let xMarkerPos = x - (crossPointX > 0 ? crossPointX : 10) - symbolSize / 2;
          if (xMarkerPos < 0) {
            xMarkerPos = 0;
          }

          circle
            .attr({
              fill: color,
              x: xMarkerPos,
              y: y - symbolSize / 2
            })
            .show();
        } else {
          lbl.hide();
          circle.hide();
        }
      }

      chart.cross.attr({ d: path }).show();
    });

    const shown = _.filter(chart.pLbls, l => l && l.circle && l.visibility !== 'hidden');
    if (shown.length) {
      const left = _.min(shown.map(l => l.circle.attr('x'))) - 8;
      let bottom = -Infinity;
      _.sortBy(shown, l => l.circle.attr('y')).forEach(l => {
        const y = Math.max(l.circle.attr('y') + symbolSize / 2 - l.height / 2, bottom + 3);
        l.attr({ x: Math.max(0, left - l.width), y });
        bottom = y + l.height;
      });
    }
  }

  drawArrivalLine(e) {
    const chart = e.target;

    if (!this.props.stats || !this.props.stats.length) {
      return true;
    }

    destroyLine(chart, 'arrival');
    destroyLine(chart, 'departure');

    if (!this.props.arrivalDepartureLines || !chart.series.length) {
      return true;
    }

    const arrivalSerie = _.first(chart.series);
    const arrivalRate = _.first(this.props.stats).avgArrivalRate;
    drawLine(chart, 'arrival', `${arrivalRate.toFixed(2)} items/day`, arrivalSerie, labelFont(this.props.wallmode, { fontWeight: 600 }));

    let doneData = _(chart.series).filter(
      s => this.props.dashboard.done.indexOf(s.userOptions.listId) > -1 || s.userOptions.listId === 'done'
    );
    if (doneData.value().length === 0) {
      return true;
    }

    doneData = doneData
      .map(d => d.data)
      .flatten()
      .filter(d => d.y > 0)
      .orderBy('x')
      .groupBy('x')
      .toPairs()
      .map(d => d[1])
      .value();

    let firstPoint = _.minBy(_.first(doneData), 'plotY');
    const lastPoint = _.minBy(_.last(doneData), 'plotY');

    if (!firstPoint) {
      return true;
    }

    if (firstPoint.index > 0) {
      firstPoint = firstPoint.series.data[firstPoint.index - 1];
    }

    const departureRate = _.last(this.props.stats).avgArrivalRate;
    drawLine(
      chart,
      'departure',
      `${departureRate.toFixed(2)} items/day`,
      { data: [firstPoint, lastPoint] },
      labelFont(this.props.wallmode, { fontWeight: 600 })
    );
  }

  render() {
    return (
      <div id={this.props.id} className="chart_content">
        <div id={`${this.props.id}-details`} className={'chart_details chart_details-full'}></div>
      </div>
    );
  }
}

const mapStateToProps = state => {
  return {
    dates: state.filters.dates,
    dateFormat: state.filters.dateFormat,
    excludeWeekends: state.filters.excludeWeekends,
    wipStagesTooltip: state.filters.cfd.wipStagesTooltip,
    approxAvgStagesTooltip: state.filters.cfd.approxAvgStagesTooltip,
    arrivalDepartureLines: state.filters.cfd.arrivalDepartureLines,
    dashboard: state.dashboards.selected,
    wallmode: state.app.wallmode
  };
};

export default withChartExport(connect(mapStateToProps)(CFDChart));
